import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
} from '@mui/material';
import { useLazyQuery } from '@apollo/client/react';
import { useAppSelector } from '../../store/hooks';
import { GET_CLARIFICATION_SCHEMAS_LIST } from '../../graphql/queries/clarificationSchemas.queries';
import {
  ClarificationSchemaListResponse,
  FindClarificationSchemaListInput,
  ClarificationSchema,
} from '../../types/clarificationSchemas.types';

const TYPE_OPTIONS = ['single', 'compound', 'base'];

export interface ClarificationSchemaFormData {
  id?: string;
  entity: string;
  matchEntities: string[];
  type: string;
  relatedEntities: string[];
  finalQueryTemplate: string;
  personKeywords: string[];
  description: string;
  triggerKeywords: string[];
  maxResultsWithoutClarification: number;
  parentSchemaId: string | null;
}

interface CreateEditClarificationSchemaModalProps {
  open: boolean;
  isEdit: boolean;
  onClose: () => void;
  onSave: (data: ClarificationSchemaFormData) => void;
  saving: boolean;
  saveError: string | null;
}

const CreateEditClarificationSchemaModal: React.FC<CreateEditClarificationSchemaModalProps> = ({
  open,
  isEdit,
  onClose,
  onSave,
  saving,
  saveError,
}) => {
  const { selectedSchema, loading } = useAppSelector((state) => state.clarificationSchemas);

  const [parentSchemas, setParentSchemas] = useState<ClarificationSchema[]>([]);
  const [loadingParentSchemas, setLoadingParentSchemas] = useState(false);

  const [fetchAllSchemas] = useLazyQuery<
    { clarificationSchemas: ClarificationSchemaListResponse },
    { params: FindClarificationSchemaListInput }
  >(GET_CLARIFICATION_SCHEMAS_LIST, { fetchPolicy: 'network-only' });

  const [formData, setFormData] = useState({
    entity: '',
    type: '',
    matchEntities: '',
    relatedEntities: '',
    finalQueryTemplate: '',
    personKeywords: '',
    description: '',
    triggerKeywords: '',
    maxResultsWithoutClarification: 0,
    parentSchemaId: null as string | null,
  });

  useEffect(() => {
    if (open) {
      loadParentSchemas();
    }
  }, [open]);

  useEffect(() => {
    if (isEdit && selectedSchema) {
      setFormData({
        entity: selectedSchema.entity || '',
        type: selectedSchema.type || '',
        matchEntities: selectedSchema.matchEntities.join(', '),
        relatedEntities: selectedSchema.relatedEntities.join(', '),
        finalQueryTemplate: selectedSchema.finalQueryTemplate || '',
        personKeywords: selectedSchema.personKeywords?.join(', ') || '',
        description: selectedSchema.description || '',
        triggerKeywords: selectedSchema.triggerKeywords.join(', '),
        maxResultsWithoutClarification: selectedSchema.maxResultsWithoutClarification ?? 0,
        parentSchemaId: selectedSchema.parentSchemaId || null,
      });
    } else if (!isEdit) {
      setFormData({
        entity: '',
        type: '',
        matchEntities: '',
        relatedEntities: '',
        finalQueryTemplate: '',
        personKeywords: '',
        description: '',
        triggerKeywords: '',
        maxResultsWithoutClarification: 0,
        parentSchemaId: null,
      });
    }
  }, [selectedSchema, isEdit, open]);

  const loadParentSchemas = async () => {
    try {
      setLoadingParentSchemas(true);
      const { data } = await fetchAllSchemas({
        variables: {
          params: {
            search: null,
            page: 1,
            pageSize: 1000,
            sort: { field: 'entity', order: 'asc' },
          },
        },
      });
      if (data?.clarificationSchemas?.success && data.clarificationSchemas.data) {
        setParentSchemas(data.clarificationSchemas.data.schemas);
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
    } finally {
      setLoadingParentSchemas(false);
    }
  };

  const parentSchemaOptions = isEdit && selectedSchema
    ? parentSchemas.filter((s) => s.id !== selectedSchema.id)
    : parentSchemas;

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const keywords = formData.triggerKeywords
      .split(',')
      .map((k) => k.trim())
      .filter((k) => k.length > 0);

    const matchEntities = formData.matchEntities
      .split(',')
      .map((m) => m.trim())
      .filter((m) => m.length > 0);

    const relatedEntities = formData.relatedEntities
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);

    const personKeywords = formData.personKeywords
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    const data: ClarificationSchemaFormData = {
      entity: formData.entity,
      matchEntities,
      type: formData.type,
      relatedEntities,
      finalQueryTemplate: formData.finalQueryTemplate,
      personKeywords,
      description: formData.description,
      triggerKeywords: keywords,
      maxResultsWithoutClarification: formData.maxResultsWithoutClarification,
      parentSchemaId: formData.parentSchemaId,
    };

    if (isEdit && selectedSchema) {
      data.id = selectedSchema.id;
    }

    onSave(data);
  };

  const isFormValid = formData.entity && formData.description && formData.type;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Esquema de Clarificación' : 'Crear Esquema de Clarificación'}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}

            <TextField
              label="Entidad"
              value={formData.entity}
              onChange={(e) => handleChange('entity', e.target.value)}
              fullWidth
              required
              disabled={saving}
            />

            <Autocomplete
              options={TYPE_OPTIONS}
              value={formData.type || null}
              onChange={(_event, value) => handleChange('type', value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipo"
                  required
                />
              )}
              disabled={saving}
            />

            <TextField
              label="Match Entities (separadas por comas)"
              value={formData.matchEntities}
              onChange={(e) => handleChange('matchEntities', e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={saving}
              helperText="Ingrese las entidades separadas por comas"
            />

            <TextField
              label="Related Entities (separadas por comas)"
              value={formData.relatedEntities}
              onChange={(e) => handleChange('relatedEntities', e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={saving}
              helperText="Ingrese las entidades relacionadas separadas por comas"
            />

            <TextField
              label="Final Query Template"
              value={formData.finalQueryTemplate}
              onChange={(e) => handleChange('finalQueryTemplate', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={saving}
            />

            <TextField
              label="Person Keywords (separadas por comas)"
              value={formData.personKeywords}
              onChange={(e) => handleChange('personKeywords', e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={saving}
              helperText="Ingrese las palabras clave de persona separadas por comas"
            />

            <TextField
              label="Descripción"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              disabled={saving}
            />

            <TextField
              label="Palabras Clave (separadas por comas)"
              value={formData.triggerKeywords}
              onChange={(e) => handleChange('triggerKeywords', e.target.value)}
              fullWidth
              required
              multiline
              rows={2}
              disabled={saving}
              helperText="Ingrese las palabras clave separadas por comas"
            />

            <TextField
              label="Máx. Resultados sin Clarificación"
              type="number"
              value={formData.maxResultsWithoutClarification}
              onChange={(e) => handleChange('maxResultsWithoutClarification', parseInt(e.target.value, 10) || 0)}
              fullWidth
              disabled={saving}
              inputProps={{ min: 0 }}
            />

            <Autocomplete
              options={parentSchemaOptions}
              getOptionLabel={(option) => option.entity}
              value={parentSchemaOptions.find((s) => s.id === formData.parentSchemaId) || null}
              onChange={(_event, value) => setFormData((prev) => ({ ...prev, parentSchemaId: value?.id || null }))}
              loading={loadingParentSchemas}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Esquema Padre"
                  helperText="Seleccione el esquema padre (opcional)"
                />
              )}
              disabled={saving}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !isFormValid}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditClarificationSchemaModal;
